import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSettingsQueriesController } from '../state/useSettingsQueriesController'

const ALL_PLATFORMS = ['facebook', 'x', 'threads', 'news'] as const

interface SettingsQueriesTabProps {
  readOnly: boolean
}

export function SettingsQueriesTab({ readOnly }: SettingsQueriesTabProps) {
  const { t } = useTranslation('settings')
  const {
    topicId,
    topic,
    isLoading,
    inputValue,
    setInputValue,
    submitAdd,
    removeKeyword,
    isAdding,
    isRemoving,
  } = useSettingsQueriesController()

  if (!topicId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-card px-6 py-16 text-center">
        <p className="text-sm text-muted-foreground">{t('queries.noTopic')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card px-6 py-8">
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-10 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  if (!topic) return null

  const displayedPlatforms: readonly string[] =
    topic.platform === 'all' ? ALL_PLATFORMS : [topic.platform]

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">
            {t('queries.keywords')}
          </h2>
        </div>

        <div className="px-6 py-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            {topic.keywords.map((keyword) => (
              <span
                key={keyword}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-sm text-foreground',
                )}
              >
                {keyword}
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={t('queries.remove')}
                    disabled={isRemoving}
                    onClick={() => removeKeyword(keyword)}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
                  >
                    <X size={13} />
                  </button>
                )}
              </span>
            ))}
          </div>

          {!readOnly && (
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                placeholder={t('queries.addPlaceholder')}
                disabled={isAdding}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submitAdd()
                  }
                }}
                className={cn(
                  'flex-1 rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              />
              <button
                type="button"
                disabled={!inputValue.trim() || isAdding}
                onClick={submitAdd}
                className={cn(
                  'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
                  'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
                )}
              >
                {t('queries.addKeyword')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">
            {t('queries.platforms')}
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 px-6 py-5">
          {displayedPlatforms.map((platform) => (
            <span
              key={platform}
              className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-sm text-foreground capitalize"
            >
              {platform}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">
            {t('queries.quotas')}
          </h2>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-muted-foreground">{t('queries.comingSoon')}</p>
        </div>
      </div>
    </div>
  )
}
